import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskLine, FadeUp } from "./Reveal";
import { IMAGES } from "../constants/site";

const CHAPTERS = [
  ["The Ground", "Exceptional wine starts with healthy soil. We farm the soil first, building organic matter, improving water retention, and creating a living ecosystem. What follows are healthy, resilient vines capable of handling whatever the season brings.", IMAGES.ground, "Newly planted vineyard rows in freshly tilled soil"],
  ["The Season", "Twenty-one harvests across Napa and Sonoma have taught us what numbers alone never can. By walking the same rows week after week, our vineyard managers and crews develop a deep understanding of each vineyard and what it needs throughout the season.", IMAGES.season, "Spring vine shoot reaching upward against a clear sky"],
  ["The Record", "From crew passes and spray applications to irrigation schedules, yield estimates, and safety training, every important vineyard activity is documented as it happens. Clients always have a current, complete view of their vineyard without needing to ask.", IMAGES.record, "VineOps login open on a laptop beside a Vinedresser cap, overlooking vineyard rows"],
];

export const Manifesto = () => {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section id="philosophy" data-testid="philosophy-section" className="py-28 md:py-40">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-12 md:gap-8">
          <div className="md:col-span-7">
            <FadeUp>
              <p className="text-[11px] tracking-[0.32em] uppercase text-[#B89B67] font-semibold mb-10">Our Philosophy</p>
            </FadeUp>
            <h2 className="font-display font-light text-[#1A3626] text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight max-w-[12ch]">
              <MaskLine delay={0.1}>Good farming</MaskLine>
              <MaskLine delay={0.22}>is quiet work.</MaskLine>
            </h2>
            <FadeUp delay={0.4} className="mt-12 max-w-[52ch] space-y-6">
              <p className="text-base md:text-lg leading-relaxed text-[#2C2926]/80">
                The hardest work in a vineyard often happens without fanfare. Season after season, our crews and managers quietly put in the time, judgment, and attention required to keep each property healthy, productive, and financially sustainable for generations.
              </p>
              <p className="text-base md:text-lg leading-relaxed text-[#2C2926]/80">
                Our philosophy begins with the vineyard owner's long-term goals. We balance soil health, water use, vine longevity, fruit quality, labor, and cost, then present the work clearly through clean, timely, and transparent reporting. Every recommendation comes from years of field experience, backed by current vineyard information, giving owners confidence that today's decisions are protecting both their vineyard and the investment behind it.
              </p>
            </FadeUp>
          </div>
          <div className="md:col-span-5 md:pt-24">
            <FadeUp delay={0.25}>
              <div ref={imgRef} className="relative aspect-[3/4] overflow-hidden">
                <motion.img
                  src={IMAGES.hands}
                  alt="Wine grapes ripening on the vine in a Napa Valley vineyard"
                  loading="lazy"
                  decoding="async"
                  style={{ y }}
                  className="absolute inset-0 w-full h-[112%] object-cover"
                />
              </div>
            </FadeUp>
          </div>
        </div>

        <div className="mt-28 md:mt-36 -mx-6 md:-mx-12 bg-[#F3EFE6] hairline-t hairline-b">
          <div className="max-w-[1440px] mx-auto grid md:grid-cols-3">
            {CHAPTERS.map(([title, body, img, alt], i) => (
              <FadeUp key={title} delay={i * 0.1} className="h-full">
                <div
                  data-testid={`chapter-${title.toLowerCase().replace(/\s/g, "-")}`}
                  className={`px-8 md:px-14 py-14 md:py-20 h-full ${
                    i > 0 ? "border-t md:border-t-0 md:border-l border-[#E5DEC9]" : ""
                  }`}
                >
                  {img && (
                    <div className="relative aspect-[4/3] overflow-hidden mb-10">
                      <img src={img} alt={alt} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
                    </div>
                  )}
                  <p className="text-[11px] tracking-[0.3em] uppercase text-[#B89B67] font-semibold mb-8">{title}</p>
                  <h3 className="sr-only">{title}</h3>
                  <p className="font-display font-light text-xl md:text-2xl leading-relaxed text-[#2C2926]/85">{body}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
